import React, { useEffect, useState, useRef } from 'react';
import { ChevronDown, Link as LinkIcon, ShieldAlert, EyeOff, TrendingDown, ShieldCheck, Brain, Banknote, Users, Lock, Shield, Server, Scan, Sparkles, Target, Palette, TrendingUp, Rocket, DollarSign, BarChart3, Building2, Globe, Handshake } from 'lucide-react';
export function PitchDeck() {
  const [activeSlide, setActiveSlide] = useState(0);
  const totalSlides = 14;
  const containerRef = useRef<HTMLDivElement>(null);
  // Handle Keyboard Navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't interfere if user is typing in an input (though there are none here)
      if (['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement).tagName)) return;
      const isNext = ['ArrowDown', 'ArrowRight', ' '].includes(e.key);
      const isPrev = ['ArrowUp', 'ArrowLeft'].includes(e.key);
      if (isNext || isPrev) {
        e.preventDefault();
        const slides = document.querySelectorAll('.slide');
        let targetIndex = activeSlide;
        if (isNext && activeSlide < totalSlides - 1) {
          targetIndex = activeSlide + 1;
        } else if (isPrev && activeSlide > 0) {
          targetIndex = activeSlide - 1;
        }
        if (targetIndex !== activeSlide && slides[targetIndex]) {
          slides[targetIndex].scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown, {
      passive: false
    });
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeSlide, totalSlides]);
  // Handle Intersection Observer for active dot and animations
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const index = Number(entry.target.getAttribute('data-index'));
          setActiveSlide(index);
          entry.target.classList.add('is-visible');
        }
      });
    }, {
      threshold: 0.5
    });
    const slides = document.querySelectorAll('.slide');
    slides.forEach(slide => observer.observe(slide));
    return () => observer.disconnect();
  }, []);
  const scrollToSlide = (index: number) => {
    const slides = document.querySelectorAll('.slide');
    if (slides[index]) {
      slides[index].scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };
  return <div className="w-full h-full bg-[#060b18] text-[#f0f4ff] font-body" ref={containerRef} data-id="element-1">
      {/* Navigation Dots */}
      <div className="nav-dots fixed right-6 top-1/2 -translate-y-1/2 z-50 flex flex-col gap-3" data-id="element-2">
        {Array.from({
        length: totalSlides
      }).map((_, i) => <button key={i} onClick={() => scrollToSlide(i)} className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${activeSlide === i ? 'bg-[#00c8ff] scale-125 shadow-[0_0_10px_rgba(0,200,255,0.8)]' : 'bg-[#7a8ba8]/40 hover:bg-[#7a8ba8]/80'}`} aria-label={`Go to slide ${i + 1}`} data-id="element-3" />)}
      </div>

      {/* Slide 1: Title */}
      <section className="slide" data-index="0" data-id="element-4">
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-0" data-id="element-5">
          <div className="absolute top-1/4 left-1/4 w-[40vw] h-[40vw] max-w-lg max-h-lg bg-[#00c8ff] rounded-full mix-blend-screen filter blur-[100px] opacity-20 animate-pulse-glow" data-id="element-6"></div>
          <div className="absolute bottom-1/4 right-1/4 w-[40vw] h-[40vw] max-w-lg max-h-lg bg-[#6366f1] rounded-full mix-blend-screen filter blur-[100px] opacity-20 animate-pulse-glow" style={{
          animationDelay: '2s'
        }} data-id="element-7"></div>
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_60%_at_50%_50%,#000_70%,transparent_100%)]" data-id="element-8"></div>
        </div>

        <div className="slide-content text-center" data-id="element-9">
          <div className="mb-6 animate-on-view flex justify-center" data-id="element-10">
            <img src="https://cdn.magicpatterns.com/uploads/oUNBTi9b5rsXiX3m9xY58X/blob-b68f2d9.webp" alt="FIQIS Logo" className="w-28 h-28 md:w-36 md:h-36 object-contain" style={{
            filter: 'hue-rotate(80deg) saturate(1.5) brightness(1.4)',
            mixBlendMode: 'lighten'
          }} data-id="element-11" />
          </div>
          <h1 className="font-heading text-[length:var(--title-size)] text-gradient mb-4 animate-on-view tracking-tight" data-id="element-12">
            FIQIS
          </h1>
          <p className="text-[length:var(--h3-size)] text-[#f0f4ff] font-medium mb-6 animate-on-view delay-100" data-id="element-13">
            Financial Quantum Intelligence Systems
          </p>
          <p className="text-[length:var(--body-size)] text-[#7a8ba8] max-w-2xl mx-auto animate-on-view delay-200" data-id="element-14">
            Secure Infrastructure for the Future of Banking
          </p>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-on-view delay-300 text-[#7a8ba8]" data-id="element-15">
          <span className="text-sm uppercase tracking-widest" data-id="element-16">
            Scroll to explore
          </span>
          <ChevronDown className="w-5 h-5 animate-bounce" data-id="element-17" />
        </div>
      </section>

      {/* Slide 2: The Problem */}
      <section className="slide" data-index="1" data-id="element-18">
        <div className="slide-content items-start w-full" data-id="element-19">
          <h2 className="font-heading text-[length:var(--h2-size)] mb-[var(--content-gap)] animate-on-view" data-id="element-20">
            Banks Are Under Pressure
          </h2>

          <div className="grid-responsive w-full" data-id="element-21">
            {[{
            icon: LinkIcon,
            title: 'Complex Core Integrations',
            desc: 'Legacy systems create costly, fragile connections with modern services.'
          }, {
            icon: ShieldAlert,
            title: 'Quantum Vulnerability',
            desc: 'Current encryption standards face obsolescence as quantum computing advances.'
          }, {
            icon: EyeOff,
            title: 'Limited Customer Insight',
            desc: 'Institutions lack AI-driven analytics to understand and serve customers effectively.'
          }, {
            icon: TrendingDown,
            title: 'Revenue Leakage',
            desc: 'Fintechs capture lending revenue from a $1T+ P2P market that should stay within the institution.'
          }].map((item, i) => <div key={i} className={`bg-[#111d35] border-l-4 border-[#00c8ff] p-6 rounded-r-xl shadow-lg animate-on-view transition-transform hover:scale-[1.02] delay-${(i + 1) * 100}`} data-id="element-22">
                <item.icon className="w-8 h-8 text-[#00c8ff] mb-4" data-id="element-23" />
                <h3 className="font-heading text-[length:var(--h3-size)] mb-2" data-id="element-24">
                  {item.title}
                </h3>
                <p className="text-[length:var(--body-size)] text-[#7a8ba8]" data-id="element-25">
                  {item.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Slide 3: The Solution */}
      <section className="slide" data-index="2" data-id="element-26">
        <div className="slide-content text-center" data-id="element-27">
          <h2 className="font-heading text-[length:var(--h2-size)] mb-4 animate-on-view" data-id="element-28">
            One Platform. Four Capabilities.
          </h2>
          <p className="text-[length:var(--h3-size)] text-[#7a8ba8] mb-[var(--content-gap)] animate-on-view delay-100" data-id="element-29">
            A modular SaaS platform that operates under your brand
          </p>

          <div className="flex flex-wrap justify-center gap-4 mb-[var(--content-gap)] w-full relative" data-id="element-30">
            {/* Connecting line behind pills */}
            <div className="hidden md:block absolute top-1/2 left-10 right-10 h-0.5 bg-gradient-to-r from-transparent via-[#00c8ff]/30 to-transparent -translate-y-1/2 z-0" data-id="element-31"></div>

            {[{
            icon: ShieldCheck,
            text: 'Post-Quantum Security'
          }, {
            icon: Brain,
            text: 'AI Intelligence'
          }, {
            icon: Banknote,
            text: 'Micro-Lending'
          }, {
            icon: Users,
            text: 'P2P Lending'
          }].map((item, i) => <div key={i} className={`relative z-10 flex items-center gap-3 bg-[#0d1528] border border-[rgba(0,200,255,0.15)] rounded-full px-6 py-4 shadow-lg animate-on-view delay-${(i + 1) * 100} hover:border-[#00c8ff]/50 transition-colors`} data-id="element-32">
                <item.icon className="w-6 h-6 text-[#00c8ff]" data-id="element-33" />
                <span className="font-heading font-bold text-[length:var(--body-size)]" data-id="element-34">
                  {item.text}
                </span>
              </div>)}
          </div>

          <p className="text-[length:var(--body-size)] text-[#f0f4ff] max-w-3xl mx-auto animate-on-view delay-400 bg-[#111d35]/50 p-6 rounded-xl border border-white/5" data-id="element-35">
            FiQIS delivers integrated infrastructure that simplifies
            integration, hardens security, unlocks intelligence, and generates
            revenue.
          </p>
        </div>
      </section>

      {/* Slide 4: Post-Quantum Security */}
      <section className="slide" data-index="3" data-id="element-36">
        <div className="slide-content flex-row flex-wrap md:flex-nowrap items-center gap-[var(--content-gap)]" data-id="element-37">
          <div className="flex-1 w-full animate-on-view" data-id="element-38">
            <h2 className="font-heading text-[length:var(--h2-size)] mb-2" data-id="element-39">
              Post-Quantum Ready
            </h2>
            <p className="text-[length:var(--h3-size)] text-[#00c8ff] mb-8 font-heading" data-id="element-40">
              Secure Integration Layer
            </p>

            <ul className="space-y-6" data-id="element-41">
              {[{
              icon: Lock,
              text: 'Simplifies and hardens connections between core systems and third parties'
            }, {
              icon: Shield,
              text: 'Built for post-quantum cryptographic standards from day one'
            }, {
              icon: Server,
              text: 'Reduces integration complexity and cost by up to 60%'
            }].map((item, i) => <li key={i} className="flex items-start gap-4" data-id="element-42">
                  <div className="mt-1 bg-[#111d35] p-2 rounded-lg border border-[#00c8ff]/20" data-id="element-43">
                    <item.icon className="w-6 h-6 text-[#00c8ff]" data-id="element-44" />
                  </div>
                  <span className="text-[length:var(--body-size)] text-[#f0f4ff] leading-relaxed" data-id="element-45">
                    {item.text}
                  </span>
                </li>)}
            </ul>
          </div>

          <div className="flex-1 w-full flex justify-center animate-on-view delay-200" data-id="element-46">
            <div className="relative w-64 h-64 md:w-80 md:h-80" data-id="element-47">
              <div className="absolute inset-0 border border-[#00c8ff]/30 rotate-45 rounded-2xl animate-pulse-glow" data-id="element-48"></div>
              <div className="absolute inset-4 border border-[#6366f1]/40 rotate-[60deg] rounded-2xl animate-spin-slow" data-id="element-49"></div>
              <div className="absolute inset-8 border border-[#8b5cf6]/50 rotate-45 rounded-2xl bg-[#111d35]/80 backdrop-blur-md flex items-center justify-center shadow-[0_0_30px_rgba(0,200,255,0.2)]" data-id="element-50">
                <ShieldCheck className="w-16 h-16 text-[#00c8ff]" data-id="element-51" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Slide 5: AI Intelligence */}
      <section className="slide" data-index="4" data-id="element-52">
        <div className="slide-content flex-row flex-wrap md:flex-nowrap items-center gap-[var(--content-gap)]" data-id="element-53">
          <div className="flex-1 w-full flex justify-center order-2 md:order-1 animate-on-view delay-200" data-id="element-54">
            <div className="relative w-64 h-64 md:w-80 md:h-80" data-id="element-55">
              {/* Abstract Nodes */}
              <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" data-id="element-56">
                <line x1="50" y1="20" x2="20" y2="70" stroke="rgba(0,200,255,0.2)" strokeWidth="1" data-id="element-57" />
                <line x1="50" y1="20" x2="80" y2="70" stroke="rgba(99,102,241,0.2)" strokeWidth="1" data-id="element-58" />
                <line x1="20" y1="70" x2="80" y2="70" stroke="rgba(139,92,246,0.2)" strokeWidth="1" data-id="element-59" />
              </svg>
              <div className="absolute top-[10%] left-1/2 -translate-x-1/2 w-16 h-16 rounded-full border border-[#00c8ff] bg-[#0d1528] flex items-center justify-center z-10 animate-float shadow-[0_0_15px_rgba(0,200,255,0.3)]" data-id="element-60">
                <Brain className="w-8 h-8 text-[#00c8ff]" data-id="element-61" />
              </div>
              <div className="absolute bottom-[20%] left-[10%] w-12 h-12 rounded-full border border-[#6366f1] bg-[#0d1528] flex items-center justify-center z-10 animate-float-delayed" data-id="element-62">
                <Scan className="w-6 h-6 text-[#6366f1]" data-id="element-63" />
              </div>
              <div className="absolute bottom-[20%] right-[10%] w-12 h-12 rounded-full border border-[#8b5cf6] bg-[#0d1528] flex items-center justify-center z-10 animate-float" data-id="element-64">
                <Target className="w-6 h-6 text-[#8b5cf6]" data-id="element-65" />
              </div>
            </div>
          </div>

          <div className="flex-1 w-full order-1 md:order-2 animate-on-view" data-id="element-66">
            <h2 className="font-heading text-[length:var(--h2-size)] mb-8" data-id="element-67">
              AI-Driven Customer Intelligence
            </h2>
            <ul className="space-y-6" data-id="element-68">
              {[{
              icon: Scan,
              text: 'Proprietary trust scoring within institutional boundaries'
            }, {
              icon: Sparkles,
              text: 'Explainable analytics for regulatory compliance'
            }, {
              icon: Target,
              text: 'Intelligent product matching to serve customers better'
            }].map((item, i) => <li key={i} className="flex items-start gap-4" data-id="element-69">
                  <div className="mt-1 bg-[#111d35] p-2 rounded-lg border border-[#6366f1]/20" data-id="element-70">
                    <item.icon className="w-6 h-6 text-[#6366f1]" data-id="element-71" />
                  </div>
                  <span className="text-[length:var(--body-size)] text-[#f0f4ff] leading-relaxed" data-id="element-72">
                    {item.text}
                  </span>
                </li>)}
            </ul>
          </div>
        </div>
      </section>

      {/* Slide 6: Revenue Engines */}
      <section className="slide" data-index="5" data-id="element-73">
        <div className="slide-content w-full" data-id="element-74">
          <h2 className="font-heading text-[length:var(--h2-size)] mb-6 text-center animate-on-view" data-id="element-75">
            Unlock New Revenue Streams
          </h2>

          <div className="flex justify-center gap-4 mb-[var(--content-gap)] animate-on-view delay-100" data-id="element-76">
            <div className="bg-[#0d1528] border-l-2 border-[#00c8ff] px-4 py-2 rounded-full text-[length:var(--small-size)] text-[#f0f4ff]" data-id="element-77">
              $565B BNPL Market by 2026
            </div>
            <div className="bg-[#0d1528] border-l-2 border-[#6366f1] px-4 py-2 rounded-full text-[length:var(--small-size)] text-[#f0f4ff]" data-id="element-78">
              $1T+ P2P Lending by 2030
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-[var(--content-gap)] w-full max-w-5xl mx-auto" data-id="element-79">
            <div className="bg-[#111d35] rounded-xl p-8 border-gradient-top shadow-xl animate-on-view delay-200 hover:scale-[1.02] transition-transform" data-id="element-80">
              <Banknote className="w-12 h-12 text-[#00c8ff] mb-6" data-id="element-81" />
              <h3 className="font-heading text-[length:var(--h3-size)] mb-6 text-white" data-id="element-82">
                Micro-Lending
              </h3>
              <ul className="space-y-4" data-id="element-83">
                {['Bank-owned infrastructure', 'Institution retains 100% of lending revenue', 'AI-driven risk assessment via trust scoring'].map((text, i) => <li key={i} className="flex items-start gap-3 text-[#7a8ba8] text-[length:var(--body-size)]" data-id="element-84">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#00c8ff] mt-2.5 flex-shrink-0" data-id="element-85" />
                    <span data-id="element-86">{text}</span>
                  </li>)}
              </ul>
            </div>

            <div className="bg-[#111d35] rounded-xl p-8 border-gradient-top shadow-xl animate-on-view delay-300 hover:scale-[1.02] transition-transform" data-id="element-87">
              <Users className="w-12 h-12 text-[#6366f1] mb-6" data-id="element-88" />
              <h3 className="font-heading text-[length:var(--h3-size)] mb-6 text-white" data-id="element-89">
                P2P Lending
              </h3>
              <ul className="space-y-4" data-id="element-90">
                {['Embedded in digital banking channels', 'Fully regulated within institutional framework', 'Strengthens community engagement'].map((text, i) => <li key={i} className="flex items-start gap-3 text-[#7a8ba8] text-[length:var(--body-size)]" data-id="element-91">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#6366f1] mt-2.5 flex-shrink-0" data-id="element-92" />
                    <span data-id="element-93">{text}</span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Slide 7: White-Label Advantage */}
      <section className="slide" data-index="6" data-id="element-94">
        <div className="slide-content text-center" data-id="element-95">
          <h2 className="font-heading text-[length:var(--h2-size)] mb-[var(--content-gap)] animate-on-view" data-id="element-96">
            Your Brand. Our Infrastructure.
          </h2>

          <div className="max-w-3xl mx-auto w-full bg-[#111d35]/40 border border-white/5 rounded-2xl p-8 md:p-12 animate-on-view delay-100" data-id="element-97">
            <ul className="space-y-8 text-left inline-block" data-id="element-98">
              {[{
              icon: Palette,
              text: "Operates entirely under your institution's brand"
            }, {
              icon: ShieldCheck,
              text: 'Strengthens your security posture'
            }, {
              icon: TrendingUp,
              text: 'Unlocks new revenue streams you control'
            }, {
              icon: Rocket,
              text: 'Positions you for long-term competitiveness'
            }].map((item, i) => <li key={i} className="flex items-center gap-6 animate-on-view delay-{(i+2)*100}" data-id="element-99">
                  <div className="bg-gradient-accent p-3 rounded-full shadow-[0_0_15px_rgba(0,200,255,0.3)]" data-id="element-100">
                    <item.icon className="w-6 h-6 text-white" data-id="element-101" />
                  </div>
                  <span className="text-[length:var(--h3-size)] font-heading text-[#f0f4ff]" data-id="element-102">
                    {item.text}
                  </span>
                </li>)}
            </ul>
          </div>
        </div>
      </section>

      {/* Slide 8: Why Now */}
      <section className="slide" data-index="7" data-id="element-103">
        <div className="slide-content items-start w-full max-w-4xl mx-auto" data-id="element-104">
          <h2 className="font-heading text-[length:var(--h2-size)] mb-[var(--content-gap)] animate-on-view" data-id="element-105">
            The Window Is Open
          </h2>

          <div className="relative border-l-2 border-[#00c8ff]/30 pl-8 space-y-10 py-4" data-id="element-106">
            {['NIST mandates post-quantum encryption readiness by 2030 — most banks lack expertise or infrastructure', 'Regulators increasing pressure on AI governance and data security', 'Fintechs capturing $50B+ in lending revenue from traditional institutions', 'Customer expectations for digital-first banking at all-time high'].map((text, i) => <div key={i} className={`relative animate-on-view delay-${(i + 1) * 100}`} data-id="element-107">
                <div className="absolute -left-[41px] top-1.5 w-4 h-4 rounded-full bg-[#060b18] border-2 border-[#00c8ff] shadow-[0_0_10px_rgba(0,200,255,0.5)]" data-id="element-108" />
                <p className="text-[length:var(--h3-size)] font-heading text-[#f0f4ff] leading-tight mb-2" data-id="element-109">
                  {text.split('—')[0]}
                  {text.includes('—') && <span className="text-[#00c8ff]" data-id="element-110">
                      {' '}
                      — {text.split('—')[1]}
                    </span>}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* NEW Slide 9: Market Opportunity */}
      <section className="slide" data-index="8" data-id="element-111">
        <div className="slide-content items-start w-full" data-id="element-112">
          <h2 className="font-heading text-[length:var(--h2-size)] mb-[var(--content-gap)] animate-on-view" data-id="element-113">
            Massive Addressable Market
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 w-full" data-id="element-114">
            {[{
            icon: Globe,
            stat: '$565B',
            desc: 'Global BNPL market by 2026 (Statista)'
          }, {
            icon: Users,
            stat: '$1T+',
            desc: 'P2P lending market by 2030 (Allied Market Research)'
          }, {
            icon: Building2,
            stat: '11,000+',
            desc: 'U.S. banks and credit unions (FDIC/NCUA)'
          }, {
            icon: TrendingUp,
            stat: '200–300',
            desc: 'New financial institutions chartered annually (CSBS, 2023)'
          }, {
            icon: ShieldCheck,
            stat: '2030',
            desc: 'NIST post-quantum encryption transition deadline'
          }].map((item, i) => <div key={i} className={`bg-[#111d35] p-6 rounded-xl border border-white/5 shadow-lg animate-on-view transition-transform hover:scale-[1.02] delay-${(i + 1) * 100}`} data-id="element-115">
                <item.icon className="w-8 h-8 text-[#00c8ff] mb-4" data-id="element-116" />
                <h3 className="font-heading text-[length:var(--h2-size)] text-[#00c8ff] mb-2" data-id="element-117">
                  {item.stat}
                </h3>
                <p className="text-[length:var(--small-size)] text-[#7a8ba8]" data-id="element-118">
                  {item.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* NEW Slide 10: Go-to-Market */}
      <section className="slide" data-index="9" data-id="element-119">
        <div className="slide-content items-start w-full" data-id="element-120">
          <h2 className="font-heading text-[length:var(--h2-size)] mb-[var(--content-gap)] animate-on-view" data-id="element-121">
            Go-to-Market Strategy
          </h2>

          <div className="grid md:grid-cols-2 gap-[var(--content-gap)] w-full max-w-5xl mx-auto" data-id="element-122">
            {[{
            icon: Handshake,
            title: 'Direct Outreach',
            desc: 'Targeted engagement with mid-sized banks and credit unions'
          }, {
            icon: Building2,
            title: 'Core Provider Partnerships',
            desc: 'Integration partnerships with FIS, Jack Henry, and Fiserv'
          }, {
            icon: Server,
            title: 'API-First Architecture',
            desc: 'SOA platform design for maximum flexibility and rapid deployment'
          }, {
            icon: Rocket,
            title: 'Pilot Programs',
            desc: 'Early adoption programs with discount incentives to drive initial traction'
          }].map((item, i) => <div key={i} className={`bg-[#111d35] border-l-4 border-[#6366f1] p-6 rounded-xl shadow-lg animate-on-view transition-transform hover:scale-[1.02] delay-${(i + 1) * 100}`} data-id="element-123">
                <item.icon className="w-8 h-8 text-[#6366f1] mb-4" data-id="element-124" />
                <h3 className="font-heading text-[length:var(--h3-size)] mb-2" data-id="element-125">
                  {item.title}
                </h3>
                <p className="text-[length:var(--body-size)] text-[#7a8ba8]" data-id="element-126">
                  {item.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* NEW Slide 11: Revenue Projections */}
      <section className="slide" data-index="10" data-id="element-127">
        <div className="slide-content w-full" data-id="element-128">
          <div className="text-center mb-[var(--content-gap)] animate-on-view" data-id="element-129">
            <h2 className="font-heading text-[length:var(--h2-size)] mb-2" data-id="element-130">
              10-Year Revenue Trajectory
            </h2>
            <p className="text-[length:var(--body-size)] text-[#7a8ba8]" data-id="element-131">
              SaaS ARR ($300K/institution) + Transaction Revenue
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 w-full max-w-6xl mx-auto mb-12" data-id="element-132">
            {[{
            year: 'Year 1',
            rev: '$300K',
            inst: '1 Institution'
          }, {
            year: 'Year 3',
            rev: '$8M',
            inst: '20 Institutions'
          }, {
            year: 'Year 5',
            rev: '$80M',
            inst: '200 Institutions'
          }, {
            year: 'Year 10',
            rev: '$1.05B+',
            inst: '2,500 Institutions'
          }].map((item, i) => <div key={i} className={`bg-[#111d35] rounded-xl p-6 border-gradient-top shadow-lg animate-on-view delay-${(i + 1) * 100}`} data-id="element-133">
                <p className="text-[length:var(--small-size)] text-[#7a8ba8] mb-2 uppercase tracking-wider" data-id="element-134">
                  {item.year}
                </p>
                <h3 className="font-heading text-[length:var(--h2-size)] text-gradient mb-2" data-id="element-135">
                  {item.rev}
                </h3>
                <p className="text-[length:var(--small-size)] text-[#7a8ba8]" data-id="element-136">
                  {item.inst}
                </p>
              </div>)}
          </div>

          {/* CSS Growth Bar */}
          <div className="w-full max-w-6xl mx-auto relative h-12 animate-on-view delay-500" data-id="element-137">
            <div className="absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-[#00c8ff] to-[#6366f1] -translate-y-1/2 rounded-full" data-id="element-138"></div>
            <div className="absolute top-1/2 left-[0%] w-3 h-3 bg-[#00c8ff] rounded-full -translate-y-1/2 -translate-x-1/2 shadow-[0_0_10px_rgba(0,200,255,0.8)]" data-id="element-139"></div>
            <div className="absolute top-1/2 left-[33%] w-4 h-4 bg-[#21a6f6] rounded-full -translate-y-1/2 -translate-x-1/2 shadow-[0_0_10px_rgba(33,166,246,0.8)]" data-id="element-140"></div>
            <div className="absolute top-1/2 left-[66%] w-5 h-5 bg-[#4286ed] rounded-full -translate-y-1/2 -translate-x-1/2 shadow-[0_0_10px_rgba(66,134,237,0.8)]" data-id="element-141"></div>
            <div className="absolute top-1/2 left-[100%] w-6 h-6 bg-[#6366f1] rounded-full -translate-y-1/2 -translate-x-1/2 shadow-[0_0_15px_rgba(99,102,241,0.8)]" data-id="element-142"></div>
          </div>
        </div>
      </section>

      {/* NEW Slide 12: The Ask */}
      <section className="slide" data-index="11" data-id="element-143">
        <div className="slide-content text-center" data-id="element-144">
          <h2 className="font-heading text-[length:var(--h2-size)] mb-8 animate-on-view" data-id="element-145">
            Investment Opportunity
          </h2>

          <div className="mb-12 animate-on-view delay-100" data-id="element-146">
            <div className="font-heading text-[length:var(--title-size)] text-gradient leading-none mb-4" data-id="element-147">
              $4M
            </div>
            <div className="text-[#00c8ff] font-heading text-[length:var(--h3-size)]" data-id="element-148">
              Seed Round at 15% Equity
            </div>
          </div>

          <div className="bg-[#111d35]/40 border border-white/5 rounded-2xl p-8 max-w-4xl mx-auto w-full animate-on-view delay-200" data-id="element-149">
            <p className="text-[length:var(--small-size)] text-[#7a8ba8] uppercase tracking-widest mb-6" data-id="element-150">
              Use of Funds
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-id="element-151">
              {[{
              icon: Brain,
              label: 'Platform Development'
            }, {
              icon: Users,
              label: 'Team Expansion'
            }, {
              icon: Handshake,
              label: 'Go-to-Market'
            }, {
              icon: ShieldCheck,
              label: 'Compliance & Security'
            }].map((item, i) => <div key={i} className="flex flex-col items-center gap-3" data-id="element-152">
                  <div className="bg-[#111d35] p-4 rounded-full border border-white/5" data-id="element-153">
                    <item.icon className="w-6 h-6 text-[#6366f1]" data-id="element-154" />
                  </div>
                  <span className="text-[length:var(--small-size)] text-[#7a8ba8] text-center" data-id="element-155">
                    {item.label}
                  </span>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Slide 13: Team (formerly Slide 9) */}
      <section className="slide" data-index="12" data-id="element-156">
        <div className="slide-content w-full" data-id="element-157">
          <h2 className="font-heading text-[length:var(--h2-size)] mb-[var(--content-gap)] text-center animate-on-view" data-id="element-158">
            Leadership
          </h2>

          <div className="grid md:grid-cols-2 gap-[var(--content-gap)] w-full max-w-5xl mx-auto" data-id="element-159">
            {/* Arthur */}
            <div className="bg-[#111d35] rounded-xl p-8 border border-white/5 animate-on-view delay-100 hover:scale-[1.02] transition-transform" data-id="element-160">
              <div className="flex items-center gap-4 mb-6" data-id="element-161">
                <div className="w-16 h-16 rounded-full bg-gradient-accent flex items-center justify-center text-xl font-heading font-bold text-white shadow-lg" data-id="element-162">
                  AM
                </div>
                <div data-id="element-163">
                  <h3 className="font-heading text-[length:var(--h3-size)] text-white" data-id="element-164">
                    Arthur Miller
                  </h3>
                  <p className="text-[#00c8ff] font-medium text-[length:var(--small-size)] uppercase tracking-wider" data-id="element-165">
                    CEO & Founder
                  </p>
                  <a href="https://www.linkedin.com/in/arthur-miller-64b32165/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[length:var(--small-size)] text-[#7a8ba8] hover:text-[#00c8ff] transition-colors mt-1" data-id="element-166">
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor" data-id="element-167">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" data-id="element-168" />
                    </svg>
                    LinkedIn
                  </a>
                </div>
              </div>
              <p className="text-[#f0f4ff] mb-6 text-[length:var(--body-size)] italic border-l-2 border-[#6366f1] pl-4" data-id="element-169">
                "Enterprise architect with deep experience in secure financial
                infrastructure"
              </p>
              <ul className="space-y-3" data-id="element-170">
                {['Key architect of Zelle® and RTP infrastructure', 'Former CTO of white-label BNPL fintech', 'Defense systems experience (DISA via Tasc/Engility)'].map((text, i) => <li key={i} className="flex items-start gap-2 text-[#7a8ba8] text-[length:var(--small-size)]" data-id="element-171">
                    <span className="text-[#00c8ff] mt-1" data-id="element-172">•</span>
                    <span data-id="element-173">{text}</span>
                  </li>)}
              </ul>
            </div>

            {/* Mark */}
            <div className="bg-[#111d35] rounded-xl p-8 border border-white/5 animate-on-view delay-200 hover:scale-[1.02] transition-transform" data-id="element-174">
              <div className="flex items-center gap-4 mb-6" data-id="element-175">
                <div className="w-16 h-16 rounded-full bg-gradient-accent flex items-center justify-center text-xl font-heading font-bold text-white shadow-lg" data-id="element-176">
                  MY
                </div>
                <div data-id="element-177">
                  <h3 className="font-heading text-[length:var(--h3-size)] text-white" data-id="element-178">
                    Mark Youwanes
                  </h3>
                  <p className="text-[#00c8ff] font-medium text-[length:var(--small-size)] uppercase tracking-wider" data-id="element-179">
                    Co-Founder & VP Eng
                  </p>
                  <a href="https://www.linkedin.com/in/mark-youwanes-0b62898/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[length:var(--small-size)] text-[#7a8ba8] hover:text-[#00c8ff] transition-colors mt-1" data-id="element-180">
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor" data-id="element-181">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" data-id="element-182" />
                    </svg>
                    LinkedIn
                  </a>
                </div>
              </div>
              <p className="text-[#f0f4ff] mb-6 text-[length:var(--body-size)] italic border-l-2 border-[#6366f1] pl-4" data-id="element-183">
                "Engineering leader specializing in scalable platforms"
              </p>
              <ul className="space-y-3" data-id="element-184">
                {['Expert in cloud-native and microservice architectures', 'Led teams deploying enterprise platforms at scale', 'Deep experience in secure API and distributed systems'].map((text, i) => <li key={i} className="flex items-start gap-2 text-[#7a8ba8] text-[length:var(--small-size)]" data-id="element-185">
                    <span className="text-[#00c8ff] mt-1" data-id="element-186">•</span>
                    <span data-id="element-187">{text}</span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Slide 14: CTA (formerly Slide 10) */}
      <section className="slide" data-index="13" data-id="element-188">
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-0" data-id="element-189">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60vw] h-[60vw] max-w-2xl max-h-2xl bg-[#00c8ff] rounded-full mix-blend-screen filter blur-[120px] opacity-10 animate-pulse-glow" data-id="element-190"></div>
        </div>

        <div className="slide-content text-center" data-id="element-191">
          <h2 className="font-heading text-[length:var(--title-size)] mb-6 animate-on-view leading-tight max-w-4xl mx-auto" data-id="element-192">
            Let's Build the Future of Banking Together
          </h2>
          <p className="text-[length:var(--h3-size)] text-[#7a8ba8] mb-12 animate-on-view delay-100" data-id="element-193">
            Ready to modernize your institution's infrastructure?
          </p>

          <a href="mailto:arthur@fiqis.com" className="inline-block bg-gradient-accent text-white font-heading font-bold text-[length:var(--body-size)] px-8 py-4 rounded-full shadow-[0_0_20px_rgba(0,200,255,0.4)] hover:shadow-[0_0_30px_rgba(0,200,255,0.6)] hover:scale-105 transition-all duration-300 animate-on-view delay-200" data-id="element-194">
            arthur@fiqis.com
          </a>

          <div className="absolute bottom-12 left-1/2 -translate-x-1/2 animate-on-view delay-300 flex flex-col items-center gap-3" data-id="element-195">
            <img src="https://cdn.magicpatterns.com/uploads/oUNBTi9b5rsXiX3m9xY58X/blob-b68f2d9.webp" alt="FIQIS Logo" className="w-12 h-12 object-contain opacity-30" style={{
            filter: 'hue-rotate(80deg) saturate(1.5) brightness(1.4)',
            mixBlendMode: 'lighten'
          }} data-id="element-196" />
            <span className="font-heading text-2xl font-bold tracking-widest text-white/20" data-id="element-197">
              FIQIS
            </span>
          </div>
        </div>
      </section>
    </div>;
}